<template>
  <div
    class="hero__carousel-wrapper"
    @click.once="disableAutoPlay"
  >
    <CarouselComponent
      id="year-in-review-carousel"
      ref="yearInReviewCarousel"
      class="year-in-review-carousel"
      description="a collection of membership statistics"
      :enable-boundary-bounce="true"
      :slides="slides"
      :transition-type="prefersReducedMotion ? 'fade' : 'default'"
      :dots="true"
      data-ui="year-in-review-carousel"
      height="auto"
      @arrow-click="handleArrowClick"
    >
      <template
        #slide="{
          eyebrow,
          topText,
          statistic,
          bottomText,
        }"
      >
        <div
          class="year-in-review-slide"
          data-ui="year-in-review-slide"
        >
          <CdrText class="year-in-review-slide__eyebrow">
            {{ eyebrow }}
          </CdrText>
          <div
            class="year-in-review-slide__heading"
            v-html="topText"
          />
          <CdrText class="year-in-review-slide__statistic">
            {{ statistic }}
          </CdrText>
          <div
            class="year-in-review-slide__subheading"
            v-html="bottomText"
          />
        </div>
      </template>
      <template #dot="{ handleClick, ...rest }">
        <button
          v-bind="rest"
          :aria-label="`Slide ${rest.slideIndex} of ${slides.length}`"
          class="year-in-review-carousel__dot
              carousel-component__dot-button carousel-component__dot-button--default"
          @click="handleClick"
        />
      </template>
      <template #arrow="{ handleClick, ...rest }">
        <CdrButton
          :icon-only="true"
          :with-background="true"
          v-bind="rest"
          @click.prevent="handleClick"
        >
          <icon-arrow-left v-if="rest.class.includes('left')" />
          <icon-arrow-right v-else />
        </CdrButton>
      </template>
    </CarouselComponent>
  </div>
</template>

<script setup>
import {
  computed,
  getCurrentInstance,
  onMounted,
  onUnmounted,
  ref,
} from 'vue';
import {
  CdrButton,
  CdrText,
  IconArrowLeft,
  IconArrowRight,
} from '@rei/cedar';
import { CarouselComponent, Vue3TouchEvents } from '@rei/carousel';
import { win } from '@rei/browser-shim';
import { metricsLink } from '../../../utils/analytics';

const props = defineProps({
  slides: { type: Array, required: false, default: () => ([]) },
});

const instance = getCurrentInstance();
const vueInstance = instance.appContext.app;
vueInstance.use(Vue3TouchEvents);

const carouselAutoPlayIntervalId = ref(null);
const carouselAutoPlayIndex = ref(0);
const yearInReviewCarousel = ref(0);

/* c8 ignore start */
const prefersReducedMotion = computed(() => win?.matchMedia
    && (win.matchMedia('(prefers-reduced-motion: reduce)') === true
    || win.matchMedia('(prefers-reduced-motion: reduce)').matches === true));

const triggerRightArrow = () => {
  yearInReviewCarousel.value?.changeIndex(1);
};

const triggerFirstSlide = () => {
  yearInReviewCarousel.value?.changeIndex(-(props.slides.length - 1));
};

const disableAutoPlay = () => {
  if (carouselAutoPlayIntervalId.value) {
    clearInterval(carouselAutoPlayIntervalId.value);
  }
};

const autoPlayCarousel = () => {
  if (carouselAutoPlayIndex.value < props.slides.length - 1) {
    triggerRightArrow();
    carouselAutoPlayIndex.value += 1;
  } else {
    triggerFirstSlide();
    carouselAutoPlayIndex.value = 0;
  }
};

const enableAutoPlay = () => {
  if (typeof yearInReviewCarousel.value?.changeIndex === 'function') {
    carouselAutoPlayIntervalId.value = setInterval(
      autoPlayCarousel,
      5000 // 5 seconds
    );
  }
};

const handleArrowClick = (event) => {
  const { currIndex } = event;
  metricsLink(`yearInReviewCarousel slide-${props.slides[currIndex].props.heading}`);
};

onMounted(() => {
  if (!prefersReducedMotion.value) {
    enableAutoPlay();
  }
});

onUnmounted(() => {
  disableAutoPlay();
});
/* c8 ignore end */
</script>

<style lang="scss">
$max-width-sm: 620px;

.hero__carousel-wrapper {
  width: 100%;
  max-width: $max-width-sm;
  margin: 0 auto;

  /*
   * These styles are nested because there are other carousels on the page
   */
  .carousel-component {
    width: 100%;
    max-width: $max-width-sm;
    margin: 0 auto;
    text-align: center;
    background-color: transparentize($cdr-membership-vibrant-color-background-primary, 0.1);
    padding-top: $cdr-space-two-x;
  }

  .carousel-component__item {
    display: flex;
    align-items: center;
    justify-content: center;
    @include cdr-lg-mq-up {
      transform: translateY(-20px);
    }
  }

  .carousel-component__dots {
    position: absolute;
    top: calc(100% - 57px);
    left: 50%;
    transform: translateX(-50%);
    @include cdr-lg-mq-up {
      top: calc(100% - 77px);
    }
  }

  .carousel-component__dot-button--default {
    border: 1px solid $cdr-color-text-button-primary;
    background-color: transparent;
  }

  .carousel-component__dot-button--active {
    background-color: $cdr-color-text-button-primary;
  }

  .carousel-component__dots ol {
    height: 30px;
    display: flex;
    align-items: center;
  }

  .carousel-component__dots li {
    margin-left: $cdr-space-three-quarter-x;
    margin-right: $cdr-space-three-quarter-x;
    padding-bottom: 3px;
    display: flex;

    @include cdr-lg-mq-up {
      margin-left: 14px;
      margin-right: 14px;
    }

    .carousel-component__dot-button {
      height: 12px !important;
      width: 12px !important;
      padding: 0;
      border-color: $cdr-membership-vibrant-color-text-primary;

      &:hover,
      &:focus {
        outline: 1px solid $cdr-color-text-button-primary;
      }
    }
    .carousel-component__dot-button--active {
      background-color: $cdr-membership-vibrant-color-text-primary;
    }
  }

  .carousel-component__arrow {
    height: 24px;
    width: 24px;
    stroke: $cdr-color-icon-default;
    top: calc(100% - 56px);
    svg {
      width: 16px;
      height: 12px;
      position: absolute;
      top: 6px;
      left: 4px;
    }
    @include cdr-lg-mq-up {
      top: calc(100% - 76px);
    }
  }

  /*
   * This black magic allows the arrows to appear as though they are contained within
   * a block element that is narrower than the carousel container, though they are not
   */
  .carousel-component__arrow {
    $breakpoint-sm: 340px;
    $breakpoint-md: 700px;
    $vw: 50vw;
    $px: 160px;

    &--right {
      @media (unquote("min-width: #{$breakpoint-sm}")) {
        transform: translateX(unquote("calc(-#{$vw} + #{$px})"));
      }
      @media (unquote("min-width: #{$breakpoint-md}")) {
        transform: translateX(unquote("-#{$px}"));
      }
    }
    &--left {
      @media (unquote("min-width: #{$breakpoint-sm}")) {
        transform: translateX(unquote("calc(#{$vw} - #{$px})"));
      }
      @media (unquote("min-width: #{$breakpoint-md}")) {
        transform: translateX(unquote("#{$px}"));
      }
    }
  }
}

.year-in-review-slide {
  color: $cdr-membership-vibrant-color-text-primary;
}

.year-in-review-slide__eyebrow {
  @include cdr-text-eyebrow-100;
  margin-bottom: $cdr-space-half-x;
  @include cdr-lg-mq-up {
    margin-bottom: $cdr-space-one-x;
  }
}
.year-in-review-slide__heading {
  @include cdr-text-heading-sans-400;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: $cdr-space-half-x;
  padding: 0 $cdr-space-one-x;
  @include cdr-sm-mq-up {
    @include cdr-text-heading-sans-500;
  }
  @include cdr-lg-mq-up {
    @include cdr-text-heading-sans-600;
  }
}
.year-in-review-slide__statistic {
  color: inherit;
  @include cdr-text-heading-display-1000;
  margin-bottom: $cdr-space-half-x;
  @include cdr-sm-mq-up {
    @include cdr-text-heading-display-1100;
  }
  @include cdr-lg-mq-up {
    @include cdr-text-heading-display-1600;
    margin-bottom: 0;
  }
}
.year-in-review-slide__subheading {
  @include cdr-text-body-300;
  margin-bottom: $cdr-space-half-x;
  padding: 0 $cdr-space-one-and-a-half-x;
  @include cdr-lg-mq-up {
    margin-bottom: $cdr-space-one-x;
  }
}
.year-in-review-slide__body {
  @include cdr-text-utility-sans-100;
  margin-bottom: $cdr-space-half-x;
  @include cdr-lg-mq-up {
    margin-bottom: $cdr-space-one-x;
  }
}
.year-in-review-slide__cta {
  @include cedarSecondaryButtonOverride;

  /*
   * weird notation required because of a change to SASS
   * more info: https://sass-lang.com/d/mixed-decls
   */
  & {
    width: 100%;
    max-width: 340px;
    justify-content: center;
    margin-bottom: $cdr-space-half-x;
    @include cdr-lg-mq-up {
      width: max-content;
      margin-bottom: $cdr-space-two-x;
    }
  }
}
</style>
